import React, { useState, useEffect, useCallback } from 'react';
import { Fab, Zoom, useTheme, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { color, r } from '../../theme/tokens';

/**
 * Elegant Back to Top button
 * - Appears after scrolling past threshold
 * - Smooth scroll animation
 * - Zoom transition for appearance
 * - Responsive positioning for mobile nav bars
 */
const BackToTop = ({ 
  threshold = 400,  // Pixels to scroll before showing
  bottom = 24,      // Distance from bottom (desktop)
  right = 24,       // Distance from right
}) => {
  const theme = useTheme();
  // Bottom navigation is shown on screens smaller than md (900px)
  const isMobileWithNav = useMediaQuery(theme.breakpoints.down('md'));
  
  // Account for bottom nav bar (~56px + padding) on mobile/medium screens
  const actualBottom = isMobileWithNav ? Math.max(bottom, 80) : bottom;
  const [visible, setVisible] = useState(false);

  // Throttled scroll handler for performance
  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    setVisible(scrollY > threshold);
  }, [threshold]);

  useEffect(() => {
    // Throttle scroll events
    let ticking = false;
    
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    
    // Check initial scroll position
    handleScroll();
    
    return () => window.removeEventListener('scroll', onScroll);
  }, [handleScroll]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Zoom in={visible} timeout={300}>
      <Fab
        onClick={scrollToTop}
        size="medium"
        aria-label="Back to top"
        sx={{
          position: 'fixed',
          bottom: actualBottom,
          right,
          backgroundColor: color.paper,
          color: color.evergreen,
          border: `${r(1)} solid`,
          borderColor: alpha(color.evergreen, 0.2),
          boxShadow: `0 ${r(2)} ${r(12)} rgba(0, 0, 0, 0.08)`,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: color.evergreen,
            color: 'primary.contrastText',
            boxShadow: `0 ${r(4)} ${r(16)} ${alpha(color.evergreen, 0.25)}`,
            transform: `translateY(-${r(2)})`,
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          // Ensure it's above other content but below modals
          zIndex: theme.zIndex.speedDial,
        }}
      >
        <KeyboardArrowUpIcon />
      </Fab>
    </Zoom>
  );
};

export default BackToTop;

